import { useRef } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { HTTP_STATUS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuCircleX } from 'react-icons/lu';

import DialogBox from '~/components/DialogBox';
import { useUserDisplayName } from '~/components/Dialogs/hooks/useUserDisplayName';
import { LEAD_LIST_DEFAULT_FILTERS } from '~/constants';
import { useRemoveAssignedUserMutation } from '~/features/leadDetails/hooks/useRemoveAssignedUserMutation';

type Props = {
    advisorId: string;
    isOwnShare: boolean;
    loanRequestId: string;
    onClose: () => void;
    open: boolean;
};

export const EndAdvisorServiceDialog: React.FC<Props> = ({ advisorId, isOwnShare, loanRequestId, onClose, open }) => {
    const { t } = useTranslation();
    const initialFocusRef = useRef<HTMLDivElement>(null);
    const advisorName = useUserDisplayName(isOwnShare ? undefined : advisorId);
    const { isPending, mutateAsync } = useRemoveAssignedUserMutation(loanRequestId);
    const navigate = useNavigate();
    const isOnLeadDetails = useRouterState({
        select: (state) => state.matches.some((match) => match.routeId.startsWith('/_app/leads_/details/')),
    });

    // own share ended → advisor loses read access, leave details page
    const onEnded = () => {
        onClose();
        if (isOwnShare && isOnLeadDetails) navigate({ search: LEAD_LIST_DEFAULT_FILTERS, to: '/leads' });
    };

    return (
        <DialogBox
            title={t('LEADS.ADVISOR_SERVICE.END_TITLE')}
            open={open}
            onClose={onClose}
            initialFocusRef={initialFocusRef}
            panelClassName="max-w-[512px] rounded-lg p-6"
            dataTestId="end-advisor-service-dialog"
        >
            <div className="flex flex-col gap-6">
                <div className="text-sm text-midnight">
                    {isOwnShare && t('LEADS.ADVISOR_SERVICE.END_TEXT_OWN')}
                    {!isOwnShare && advisorName && t('LEADS.ADVISOR_SERVICE.END_TEXT', { name: advisorName })}
                    {!isOwnShare && !advisorName && t('LEADS.ADVISOR_SERVICE.END_TEXT_UNNAMED')}
                </div>

                <div className="flex justify-end gap-2">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuCircleX />}
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        iconLeft={<LuCheck />}
                        onClick={async () => {
                            // mutateAsync promise, not mutate callbacks: skipped once losing the permission unmounts this dialog
                            await mutateAsync(advisorId).then(onEnded, (error: Error & { status?: number }) => {
                                // already ended elsewhere → treat as ended
                                if (error.status === HTTP_STATUS.BAD_REQUEST) onEnded();
                            });
                        }}
                        disabled={isPending}
                        data-testid="end-advisor-service-confirm"
                    >
                        {t('LEADS.ADVISOR_SERVICE.END_CONFIRM')}
                    </Button>
                </div>
            </div>
        </DialogBox>
    );
};
